import { useEffect, useState } from "react";

/**
 * When a queued or running AI job reads as "taking longer than usual" (PREC-4052). Production, the
 * 30 days to 2026-10-05: AI drafts (PROCESS_COPYWRITING_ORCHESTRATION, 258 runs) waited p95 4.2 min
 * for a slot and ran p95 6.5 min (max 10); Deep Research (PROCESS_DEEP_RESEARCH, 176 runs) ran p95
 * 18.3 min (max 27). Each hint sits just past the running p95, as decided on the ticket.
 */
export const DRAFTS_SLOW_AFTER_MS = 8 * 60_000;
export const DEEP_RESEARCH_SLOW_AFTER_MS = 20 * 60_000;

const MINUTE_MS = 60_000;

/** The elapsed time beside a queued or running job: "<1 min" or "N min". */
export function formatElapsed(ms: number): string {
	const minutes = Math.floor(Math.max(0, ms) / MINUTE_MS);

	return minutes < 1 ? "<1 min" : `${minutes} min`;
}

/** Whether a phase that began at `since` (epoch ms) has run past `threshold`. */
export function isTakingLong(since: number | null, now: number, threshold: number): boolean {
	return since !== null && now - since > threshold;
}

/**
 * A timestamp as epoch ms, or `fallback` when there is none or it does not parse. Takes a Date
 * too: some schemas type a field as Date while the payload, which is not parsed, holds the string.
 */
export function timestampOr(value: string | Date | null | undefined, fallback: number): number {
	const parsed = value ? new Date(value).getTime() : NaN;

	return Number.isNaN(parsed) ? fallback : parsed;
}

/** The current time, re-read every `intervalMs` while `active`, so an elapsed label keeps counting. */
export function useNow(active: boolean, intervalMs = 30_000): number {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (!active) {
			return;
		}

		// A start later than the last tick reads as "<1 min" (formatElapsed clamps), until this ticks.
		const timer = setInterval(() => setNow(Date.now()), intervalMs);

		return () => clearInterval(timer);
	}, [active, intervalMs]);

	return now;
}
